import React, { useId } from "react";
import PropTypes from "prop-types";

/**
 * 開關 — 模板 `/form-advanced` 的 CSS Switch(`scss/custom/plugins/_switch.scss`)。
 *
 * 視覺與模板同一套:`input[switch]` + 相鄰 `label[data-on-label][data-off-label]`。
 *
 * ## 為什麼不靠 label 的 `htmlFor` 轉送(票 36)
 *
 * 實測(Chromium 149,production build):**使用者用滑鼠點 pill 切不動**。
 * 把事件各階段的 `checked` 印出來看得很清楚:
 *
 *   DOC-capture target=INPUT checked=true    ← 瀏覽器確實切了
 *   INPUT-target target=INPUT checked=false  ← 還沒走到 target 就被還原
 *
 * 也就是 label 把點擊轉給 input 之後,受控 checkbox 的還原機制把值收了回去,
 * 最終 state 沒更新。鍵盤按空白鍵同樣切不動,而且 `display:none` 的 input 本來
 * 就 focus 不到 —— 鍵盤與讀螢幕的人等於沒有這顆開關。
 * jest 抓不到:那邊用 `fireEvent.click(label)` 是合成事件,繞過了瀏覽器的轉送。
 *
 * 所以改成**由我們自己驅動**:label 的 click 直接算出新值並 `preventDefault()`
 * (擋掉轉送,不會切兩次);鍵盤在 input 上接 Space/Enter。input 仍然留著(改成
 * 看不見但有渲染)讓讀螢幕的人聽得到、鍵盤 focus 得到。
 *
 *   <Switch text="合約管理－合約金額" checked={on} onChange={setOn} />
 */
export const Switch = ({
  label, text, checked = false, onChange, readOnly = false, disabled = false,
  onLabel = "On", offLabel = "Off", color = "none", id,
}) => {
  const autoId = useId();
  const inputId = id || `switch-${autoId}`;
  const locked = readOnly || disabled;
  const name = label || (typeof text === "string" ? text : undefined);

  // 點 label、直接點 input、按空白鍵都走這裡,不倚賴瀏覽器把點擊轉給 input(見檔頭)
  const toggle = (e) => {
    if (locked) return;
    if (e) e.preventDefault();
    if (onChange) onChange(!checked);
  };
  const onKeyDown = (e) => {
    if (e.key === " " || e.key === "Enter" || e.key === "Spacebar") toggle(e);
  };

  return (
    <span className={`dck-switch${locked ? " is-locked" : ""}`}>
      <input
        ref={(el) => {
          if (el) el.setAttribute("switch", color || "none");
        }}
        type="checkbox"
        id={inputId}
        checked={checked}
        disabled={locked}
        // 值由 toggle 驅動;onChange 留著是為了讓 React 認得它是受控欄位(不給會噴警告)。
        // onClick 也接:直接點到 input(測試的合成事件、讀螢幕軟體的啟用)同樣要切得動。
        onChange={() => {}}
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-label={name}
      />
      <label htmlFor={inputId} data-on-label={onLabel} data-off-label={offLabel} onClick={toggle} />
      {text ? (
        <label htmlFor={inputId} className="dck-switch-text" onClick={toggle}>{text}</label>
      ) : null}
    </span>
  );
};

Switch.propTypes = {
  label: PropTypes.string,
  text: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  onLabel: PropTypes.string,
  offLabel: PropTypes.string,
  color: PropTypes.string,
  id: PropTypes.string,
};

export default Switch;
